import { Box, IconButton, Stack } from '@mui/material';
import { CommentNoteIcon, UserIcon } from '@synergy/theme-next';
import { FormattedMessage, FormattedTime } from 'react-intl';
import { PaloColumn, PaloListView, PaloStatusPill } from '@synergy/components-palo';
import { PoolButton, PoolModal, PoolTypography, ValueOrNDash } from '@synergy/components';
import { PoolTooltip } from '@synergy/components-next';
import { ReactNode, useEffect, useState } from 'react';
import { getImportSupplierStatusTranslationId } from '@synergy/models';
import { useSearchParams } from 'react-router-dom';

import { AddSupplierModal } from './import-overview-add-supplier.modal';
import { ImportSupplier } from '../../models/import-supplier';
import { deleteImportSupplier, fetchImportSuppliers } from '../../store/suppliers/import-suppliers-actions';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export const ImportOverviewSuppliers = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useThunkDispatch();
    const [importToDelete, setImportToDelete] = useState<ImportSupplier | null>(null);
    const [importToEdit, setImportToEdit] = useState<ImportSupplier | null>(null);
    const { requesting, importSuppliers: paginatedImportSuppliers } = useAppSelector(state => state.importSuppliers);

    // The list sorts on the server, so the chosen column travels in the address.
    // Until one is chosen the list claims no order of its own — an arrow over a
    // column the server is not sorting by says something untrue.
    const sortColumn = searchParams.get('sort');
    const sort = sortColumn
        ? { key: sortColumn, order: ('desc' === searchParams.get('order') ? 'desc' : 'asc') as 'asc' | 'desc' }
        : undefined;

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const day = (value?: Date): ReactNode => (
        <ValueOrNDash
            value={value}
            strictEqual={true}
            valueRender={date => <FormattedTime value={date} year="numeric" month="short" day="2-digit" />}
        />
    );

    /** Contact and note are only worth a line when there is one. */
    const marker = (title: ReactNode, icon: ReactNode, filled: boolean): ReactNode => (
        <PoolTooltip title={filled ? title : ''} placement="top" variant="light">
            <span>
                <IconButton disabled={!filled}>{icon}</IconButton>
            </span>
        </PoolTooltip>
    );

    const columns: PaloColumn<ImportSupplier>[] = [
        {
            key: 'status',
            type: 'status',
            sortKey: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: supplier => (
                <PaloStatusPill
                    status={supplier.status}
                    label={<FormattedMessage id={getImportSupplierStatusTranslationId(supplier.status)} />}
                />
            ),
        },
        {
            key: 'supplier',
            type: 'name',
            sortKey: 'supplier',
            head: <FormattedMessage id="app.imports.suppliersTable.supplier" />,
            cell: supplier => supplier.supplier.name,
        },
        {
            key: 'lastImport',
            type: 'date',
            sortKey: 'lastImport',
            head: <FormattedMessage id="app.imports.suppliersTable.lastImport" />,
            cell: supplier => day(supplier.lastImport),
        },
        {
            key: 'lastPriceAdjustment',
            type: 'date',
            sortKey: 'lastPriceAdjustment',
            head: <FormattedMessage id="app.imports.suppliersTable.lastPriceAdjustment" />,
            cell: supplier => day(supplier.lastPriceAdjustment),
        },
        {
            key: 'interval',
            type: 'classification',
            head: <FormattedMessage id="app.imports.suppliersTable.interval" />,
            cell: supplier => (
                <ValueOrNDash
                    value={supplier.importIntervalType}
                    strictEqual={true}
                    valueRender={value => <FormattedMessage id={`app.enum.importIntervalType.${value}`} />}
                />
            ),
        },
        {
            key: 'importReceptionType',
            type: 'classification',
            sortKey: 'importReceptionType',
            head: <FormattedMessage id="app.imports.suppliersTable.importReceptionType" />,
            cell: supplier => (
                <ValueOrNDash
                    value={supplier.importReceptionType}
                    strictEqual={true}
                    valueRender={value => <FormattedMessage id={`app.enum.importReceptionType.${value}`} />}
                />
            ),
        },
        {
            key: 'info',
            type: 'name',
            head: undefined,
            cell: supplier => (
                <Stack flexDirection="row">
                    {marker(
                        <Stack gap={0.5}>
                            <Box sx={{ pb: 1, fontWeight: 600 }}>
                                <FormattedMessage id="app.imports.suppliersTable.importContact" />
                            </Box>
                            <Stack>
                                <Box>{supplier.importContact?.fullName}</Box>
                                <Box color="grey.800">{supplier.importContact?.phone}</Box>
                                <Box color="grey.800">{supplier.importContact?.email}</Box>
                            </Stack>
                        </Stack>,
                        <UserIcon />,
                        !!supplier.importContact
                    )}
                    {marker(
                        <Stack gap={0.5}>
                            <Box sx={{ fontWeight: 600 }}>
                                <FormattedMessage id="app.label.internalComment" />
                            </Box>
                            {supplier.internalNote}
                        </Stack>,
                        <CommentNoteIcon />,
                        !!supplier.internalNote
                    )}
                </Stack>
            ),
        },
    ];

    useEffect(() => {
        dispatch(fetchImportSuppliers({ searchParams }));
    }, [dispatch, searchParams]);

    const handleDelete = () => {
        if (importToDelete != null) {
            dispatch(deleteImportSupplier({ importSupplierId: importToDelete?.id }));
        }
        setImportToDelete(null);
    };

    const deleteActionChildren = [
        <PoolButton key="1" styleType="firstLevel" variant="text" onClick={() => setImportToDelete(null)}>
            <FormattedMessage id="app.common.cancel" />
        </PoolButton>,
        <PoolButton key="2" styleType="firstLevel" color="secondary" onClick={handleDelete}>
            <FormattedMessage id="app.common.delete" />
        </PoolButton>,
    ];

    return (
        <>
            <PaloListView<ImportSupplier, number>
                embedded={true}
                columns={columns}
                items={paginatedImportSuppliers.items}
                getRowId={supplier => supplier.id}
                requesting={requesting}
                emptyLabel="Keine überwachten Lieferanten."
                rowActions={supplier => [
                    {
                        label: <FormattedMessage id="app.imports.suppliersTable.EditSupplier" />,
                        onClick: () => setImportToEdit(supplier),
                    },
                    { label: <FormattedMessage id="app.common.remove" />, onClick: () => setImportToDelete(supplier) },
                ]}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                pagination={{
                    count: paginatedImportSuppliers.total,
                    page: Number(searchParams.get('page') ?? 1),
                    limit: Number(searchParams.get('limit') ?? 50),
                    onPageChange: page => updateParams({ page: String(page) }),
                    onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
                }}
            />

            <AddSupplierModal
                open={!!importToEdit}
                importSupplier={importToEdit}
                onClose={() => setImportToEdit(null)}
            />
            <PoolModal
                isVisible={!!importToDelete}
                title={<FormattedMessage id="app.imports.suppliers.deleteModal.title" />}
                actionChildren={deleteActionChildren}
                handleClose={() => setImportToDelete(null)}
                hasModalTypography={true}
            >
                <PoolTypography
                    id="app.imports.suppliers.deleteModal.content"
                    values={{ name: importToDelete?.supplier.name }}
                />
            </PoolModal>
        </>
    );
};
